// 담당: 혜원 · Campaigns — 파이프라인 (Board · Calendar · History · New · Detail)
// 소스: Salesforce Opportunity(캠페인) + 기회상품(채널). 값은 실 API(fetchCampaignBoard, dev 실패 시 mock 폴백).
// 캘린더 일정/조회수·시청률은 후속 분석·SF 조인 예정 — 지금은 mock.
import { useContext, useMemo, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Clock, Info, Plus, Search, X } from 'lucide-react'
import { CreatorsHeaderSlotContext } from '../CreatorsPage'
import {
  CAMPAIGN_STAGES,
  CAMPAIGN_STAGE_LABELS,
  addCampaignContent,
  clearCampaignState,
  createCampaignDraft,
  deleteCampaignContent,
  deleteCampaignDraft,
  fetchCampaignBoard,
  setCampaignState,
  fetchCampaignContents,
  fetchCampaignDrafts,
  fetchVideoMetrics,
  youtubeVideoId,
  type Campaign,
  type CampaignBoard,
  type CampaignChannel,
  type CampaignContent,
  type VideoMetric,
  type CampaignScheduleEvent,
  type CampaignStage,
  type NewCampaignInput,
} from '../creatorsApi'

let CREATOR_ID = '0012w00000n4rUJAAY' // 셸 라우트(creatorId)로 세팅됨. 없으면 스캐폴드 기본(레오제이).

const won = (n: number) => '₩' + n.toLocaleString('en-US')
const man = (n: number) => (Number.isFinite(n) ? (n / 10000).toLocaleString('en-US') : '0') + '만'
const eok = (n: number) => (Number.isFinite(n) ? '₩' + (Math.round(n / 1e6) / 100).toFixed(1) + '억' : '—')
const pct = (r: number) => (r * 100).toFixed(1) + '%'
/** 이미 퍼센트 단위인 값(포털 video-metrics 의 watchRate)용 — pct()에 넣으면 100배가 된다. */
const pctPoint = (v: number) => v.toFixed(1) + '%'
/** 구독전환은 비율이 아니라 명 수다. 부호를 붙여 순증/순감을 드러낸다. */
const signedInt = (n: number) => `${n >= 0 ? '+' : ''}${Math.round(n).toLocaleString('ko-KR')}`
const ym = (d: string) => d.slice(2).replace(/-/g, '.').slice(0, 5)
const periodLabel = (c: Campaign) => `${ym(c.openedDate)}${c.dueDate ? ' – ' + ym(c.dueDate) : ''}`

const PHASE: Record<CampaignStage, { en: string; kr: string; color: string }> = {
  intake: { en: 'INTAKE', kr: '인테이크', color: '#8E8AE0' },
  casting: { en: 'CASTING', kr: '캐스팅', color: '#5653FA' },
  production: { en: 'PRODUCTION', kr: '프로덕션', color: '#0BC5C0' },
  live: { en: 'LIVE', kr: '라이브', color: '#C84BC9' },
  complete: { en: 'COMPLETE', kr: '정산 마감', color: '#5FC91F' }, // 핸드오프 토큰(연두)
}
// 밝은 단계색(연두) 위에는 잉크 텍스트, 나머지는 화이트 — WCAG 대비 확보
const onPhase = (_stage: CampaignStage) => '#FFFFFF'
const STAGE_MICRO = [
  'CAMPAIGN PROFILING', 'FEASIBILITY CHECK', 'CASTING MATCH', 'CAMPAIGN LOCKED',
  'PRODUCTION', 'CONTENT REVIEW', 'PUBLISHING', 'PERFORMANCE TRACKING',
]
const microLabel = (p: number) => STAGE_MICRO[Math.min(Math.max(p, 1), 8) - 1]
// 카드 하단 마이크로 상태 문구 (세부단계별)
const STATUS_MICRO = [
  '일정·예산·콘텐츠 정의중', '일정/가능여부 확인중', '브랜드–크리에이터 매칭중', '캠페인 확정 대기',
  '콘텐츠 제작중', '콘텐츠 검수중', '발행 진행', '성과 트래킹중',
]
const statusMicro = (p: number) => STATUS_MICRO[Math.min(Math.max(p, 1), 8) - 1]
// Detail·History 6-스텝 진척 인디케이터 (핸드오프)
const STEP6 = ['인입', '검토중', '조율중', '진행확정', '제작중', '업로드 완료']
const PROG_TO_STEP: Record<number, number> = { 1: 0, 2: 1, 3: 2, 4: 3, 5: 4, 6: 4, 7: 5, 8: 5 }
const stepOf = (p: number) => PROG_TO_STEP[Math.min(Math.max(p, 1), 8)] ?? 0
// 6스텝 클릭 → 대표 progress (앱-상태 지정용)
const STEP_TO_PROG = [1, 2, 3, 4, 5, 7]

const CH_PILL: Record<CampaignChannel['platform'], { label: string; bg: string; color: string }> = {
  youtube: { label: 'YouTube', bg: '#FFECEC', color: '#FF0000' },
  instagram: { label: 'Instagram', bg: '#FCE9F3', color: '#E1306C' },
  tiktok: { label: 'TikTok', bg: '#ECECEC', color: '#111111' },
  commerce: { label: 'Commerce', bg: 'rgba(86,83,250,0.1)', color: '#5653FA' },
  editorial: { label: 'Editorial', bg: '#F1EEEA', color: '#8F6A34' },
}
const OPEN_STAGES: CampaignStage[] = ['intake', 'casting', 'production', 'live']

// 플랫폼별 콘텐츠 유형 옵션 (직접입력 대신 선택)
const CONTENT_TYPES: Record<CampaignChannel['platform'], string[]> = {
  youtube: ['롱폼', '숏폼', '일반 PPL', '기획영상'],
  instagram: ['릴스', '피드', '스토리', '이미지'],
  tiktok: ['숏폼', '일반 PPL'],
  commerce: ['라이브마켓', '소셜마켓', '이커머스'],
  editorial: ['마케팅 라이선스', '이커머스 라이선스', '기타'],
}

// 앱 디자인에 맞춘 커스텀 셀렉트 (네이티브 select UI 대체 — 사각·hairline·brand-blue)
function FieldSelect({ value, options, onChange, placeholder, ariaLabel }: {
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
  placeholder?: string
  ariaLabel?: string
}) {
  const [open, setOpen] = useState(false)
  const cur = options.find((o) => o.value === value)
  return (
    <div className="relative">
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="flex h-9 w-full items-center justify-between gap-1 border border-line-hairline bg-surface-base px-3 text-sm text-fg-1 hover:border-line-soft focus:border-brand-blue focus:outline-none"
      >
        <span className={'truncate ' + (cur ? '' : 'text-fg-3')}>{cur ? cur.label : (placeholder ?? '선택')}</span>
        <ChevronDown className={'h-3.5 w-3.5 flex-none text-fg-3 transition ' + (open ? 'rotate-180' : '')} aria-hidden />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 max-h-60 w-full min-w-max overflow-y-auto border border-line-hairline bg-surface-base shadow-lg">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onMouseDown={() => { onChange(o.value); setOpen(false) }}
              className={'block w-full whitespace-nowrap px-3 py-2 text-left text-sm hover:bg-surface-soft ' + (o.value === value ? 'font-semibold text-brand-blue' : 'text-fg-2')}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── 기간 필터 (FE, openedDate 기준) ───────────────────────
// 기준 시점 = 데이터의 최신 기회 생성월(오늘 기준이면 미래·과거로 흩어져 빈 화면이 됨)
type Period = 'all' | 'year' | 'quarter' | 'month'
const PERIODS: { key: Period; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: 'year', label: '연' },
  { key: 'quarter', label: '분기' },
  { key: 'month', label: '월' },
]
const ymd = (d: string): [number, number] => {
  const [y, m] = d.split('-').map(Number)
  return [y || 0, m || 0]
}
function inPeriod(d: string, period: Period, ry: number, rm: number): boolean {
  if (period === 'all') return true
  const [y, m] = ymd(d)
  if (period === 'year') return y === ry
  if (period === 'month') return y === ry && m === rm
  return y === ry && Math.floor((m - 1) / 3) === Math.floor((rm - 1) / 3)
}
// 경과일 배지 (D+n) — 기준 시점 = 데이터의 최신 개설일(오늘 기준이면 값이 과하게 커짐)
const DAY_MS = 86400000
const parseDate = (d: string) => new Date(d + 'T00:00:00').getTime()
const dPlus = (opened: string, ref: string) => 'D+' + Math.max(0, Math.round((parseDate(ref) - parseDate(opened)) / DAY_MS))

// Board 시각 모드
type BoardMode = 'kanban' | 'list' | 'compact'
const BOARD_MODES: { key: BoardMode; label: string }[] = [
  { key: 'kanban', label: 'Kanban' },
  { key: 'list', label: 'List' },
  { key: 'compact', label: 'Compact' },
]

function deriveCampaignPeriod(campaigns: Campaign[], period: Period) {
  // 기준 시점 = 오늘 (연/분기/월 모두 실제 오늘 기준)
  const now = new Date()
  const ry = now.getFullYear()
  const rm = now.getMonth() + 1
  const tag =
    period === 'month' ? `${ry}.${String(rm).padStart(2, '0')}`
    : period === 'quarter' ? `${ry} Q${Math.floor((rm - 1) / 3) + 1}`
    : period === 'year' ? `${ry}년`
    : ''
  const filtered = period === 'all' ? campaigns : campaigns.filter((c) => inPeriod(c.openedDate, period, ry, rm))
  return { filtered, tag }
}

// ── 캘린더 mock (핸드오프 2026.03 일정) ────────────────
const EC: Record<string, string> = {
  미팅: '#8E8AE0', 촬영: '#3E3BE0', 편집: '#8E8AE0', 업로드: '#5653FA',
  라이브: '#C84BC9', 시딩: '#948586', 리포트: '#3E9B78', 마감: '#B4453C', 화보: '#FC8FFF',
}
type CalEvent = { ev: string; camp: string; tag: string }
const WEEKDAYS_KR = ['일', '월', '화', '수', '목', '금', '토']
const MONTH_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

// ── 공통 조각 ──────────────────────────────────────────
function TagChip({ tag, children }: { tag: string; children: React.ReactNode }) {
  const color = EC[tag] ?? '#5653FA'
  return (
    <div className="truncate px-1.5 py-[3px] text-[10px] font-medium leading-tight" style={{ background: color + '24', borderLeft: `3px solid ${color}`, color: '#352628' }}>
      {children}
    </div>
  )
}

// ── Board ──────────────────────────────────────────────
function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-surface-base px-4 py-3">
      <div className="text-[11px] text-fg-3">{label}</div>
      <div className="mt-1 font-display text-xl font-bold tabular-nums text-fg-1">{value}</div>
    </div>
  )
}

// 단계 배지 (NN + 라벨, 국면색 채움)
function StageBadge({ c }: { c: Campaign }) {
  const color = PHASE[c.stage].color
  const shine = c.stage !== 'complete' && c.progress < 8 // 진행중만 빛 스윕
  return (
    <span className="relative inline-flex max-w-full items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-sm px-2 py-1 font-display text-[10px] font-semibold uppercase tracking-wide" style={{ background: color, color: onPhase(c.stage) }}>
      <span className="font-bold tabular-nums">{String(c.progress).padStart(2, '0')}</span>
      <span className="truncate">{microLabel(c.progress)}</span>
      {shine && (
        <span aria-hidden className="lf-sheen pointer-events-none absolute inset-y-0 left-0 w-1/3" style={{ background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.65),transparent)' }} />
      )}
    </span>
  )
}

function LmpaTag() {
  return <span className="bg-brand-blue px-1 py-0.5 text-[8px] font-bold leading-none text-white">LMPA</span>
}

function CampaignCard({ c, refDate, onOpen, onDelete }: { c: Campaign; refDate: string; onOpen: () => void; onDelete?: () => void }) {
  const color = PHASE[c.stage].color
  const isComplete = c.stage === 'complete'
  const showBudget = c.budget != null && c.progress >= 3
  const confirmed = c.progress >= 4 || c.budgetConfirmed
  return (
    <button onClick={onOpen} className={'block w-full border bg-surface-base p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue ' + (c.isDraft ? 'border-dashed border-[rgba(86,83,250,0.5)]' : 'border-line-hairline')}>
      {/* 브랜드 · LMPA · 드래프트 · 경과일/삭제 */}
      <div className="flex items-center gap-1.5">
        <span className="truncate text-[11px] font-semibold text-fg-2">{c.brand}</span>
        {c.isLmpa && <LmpaTag />}
        {c.isDraft && <span className="bg-[rgba(86,83,250,0.12)] px-1 py-0.5 text-[8px] font-bold text-brand-blue">드래프트</span>}
        {c.commerce && <span className="bg-[rgba(86,83,250,0.1)] px-1 py-0.5 text-[8px] font-semibold text-brand-blue">커머스</span>}
        {c.isDraft && onDelete ? (
          <span
            role="button"
            tabIndex={0}
            aria-label="드래프트 삭제"
            onClick={(e) => { e.stopPropagation(); onDelete() }}
            className="ml-auto flex h-5 w-5 flex-none items-center justify-center text-fg-3 hover:text-status-danger"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </span>
        ) : !isComplete ? (
          <span className="ml-auto inline-flex flex-none items-center gap-0.5 text-[10px] font-display tabular-nums text-fg-3">
            <Clock className="h-3 w-3" aria-hidden />{dPlus(c.openedDate, refDate)}
          </span>
        ) : null}
      </div>
      <div className="mt-1 text-[15px] font-bold leading-snug text-fg-1">{c.title}</div>
      <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-display tabular-nums text-fg-3">
        <CalendarDays className="h-3 w-3" aria-hidden /> Opened {c.openedDate.replace(/-/g, '.')}
      </div>
      {/* 예산 인셋 (03단계부터: 예상 / 04+ 확정) */}
      {showBudget && (
        <div className="mt-2 border border-line-hairline px-2 py-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] text-fg-3">{confirmed ? '확정 예산' : '예상 예산 (est.)'}</span>
            <span className="font-display text-[13px] font-bold tabular-nums text-fg-1">{won(c.budget!)}</span>
          </div>
          <div className="text-right text-[9px] text-fg-3">VAT 별도</div>
        </div>
      )}
      <div className="mt-2"><StageBadge c={c} /></div>
      <div className="mt-2.5 flex items-center gap-2">
        <div className="h-1 flex-1 bg-line-hairline"><div className="h-1" style={{ width: `${(c.progress / 8) * 100}%`, background: color }} /></div>
        <span className="text-[10px] font-display tabular-nums text-fg-3">{c.progress}/8</span>
      </div>
      {/* 마이크로 상태 */}
      <div className="mt-2.5 flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 flex-none rounded-full" style={{ background: color }} aria-hidden />
        <span className="truncate text-[11px] text-fg-3">{isComplete ? '정산 마감' : statusMicro(c.progress)}</span>
      </div>
    </button>
  )
}

// List 모드 — 밀도 높은 테이블
function BoardList({ cs, onOpen }: { cs: Campaign[]; onOpen: (id: string) => void }) {
  const order = (s: CampaignStage) => CAMPAIGN_STAGES.indexOf(s)
  const rows = [...cs].sort((a, b) => order(a.stage) - order(b.stage) || a.progress - b.progress)
  return (
    <div className="overflow-x-auto border border-line-hairline bg-surface-base">
      <div className="min-w-[720px]">
        <div className="grid grid-cols-[2fr_1.5fr_1.2fr_0.6fr] border-b border-line-hairline bg-surface-soft text-[11px] font-semibold uppercase tracking-wide text-fg-3">
          <div className="px-4 py-3">PROJECT</div>
          <div className="px-4 py-3">STAGE</div>
          <div className="px-4 py-3 text-right">BUDGET</div>
          <div className="px-4 py-3 text-right">PROG.</div>
        </div>
        {rows.map((c) => {
          const confirmed = c.progress >= 4 || c.budgetConfirmed
          const color = PHASE[c.stage].color
          return (
            <button key={c.id} onClick={() => onOpen(c.id)} className="grid w-full grid-cols-[2fr_1.5fr_1.2fr_0.6fr] items-center border-b border-line-hairline text-left last:border-b-0 hover:bg-surface-soft">
              <div className="px-4 py-4">
                <div className="flex items-center gap-1.5">
                  <span className="text-[15px] font-bold text-fg-1">{c.brand}</span>
                  {c.isLmpa && <LmpaTag />}
                  {c.commerce && <span className="bg-[rgba(86,83,250,0.1)] px-1 py-0.5 text-[8px] font-semibold text-brand-blue">커머스</span>}
                </div>
                <div className="mt-0.5 truncate text-[13px] text-fg-3">{c.title}</div>
              </div>
              <div className="px-4 py-4">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-6 w-6 flex-none items-center justify-center text-[10px] font-bold font-display tabular-nums" style={{ background: color, color: onPhase(c.stage) }}>{String(c.progress).padStart(2, '0')}</span>
                  <span className="truncate font-display text-[13px] font-bold uppercase tracking-wide text-fg-1">{microLabel(c.progress)}</span>
                </div>
              </div>
              <div className="px-4 py-4 text-right">
                {c.budget != null && c.progress >= 3 ? (
                  <>
                    <div className="font-display text-[15px] font-bold tabular-nums text-fg-1">{won(c.budget)}</div>
                    <div className="mt-0.5 text-[10px] text-fg-3">{confirmed ? '확정' : '예상'} 예산 · VAT 별도</div>
                  </>
                ) : (
                  <span className="text-[13px] text-fg-3">—</span>
                )}
              </div>
              <div className="px-4 py-4 text-right font-display text-[15px] font-bold tabular-nums text-fg-1">{c.progress}/8</div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// Compact 모드 — 국면별 초압축 칩 컬럼
function BoardCompact({ byStage, onOpen }: { byStage: (s: CampaignStage) => Campaign[]; onOpen: (id: string) => void }) {
  return (
    <div className="-mx-8 overflow-x-auto px-8 pb-1">
      <div className="grid grid-cols-[repeat(5,minmax(200px,1fr))] gap-3">
      {CAMPAIGN_STAGES.map((stage) => {
        const items = byStage(stage)
        const meta = PHASE[stage]
        const budgetTotal = items.reduce((n, c) => n + (c.budget ?? 0), 0)
        return (
          <div key={stage} className="min-w-0">
            <div className="mb-2 flex items-center gap-2 border-b-2 pb-1.5" style={{ borderColor: meta.color }}>
              <span className="h-1.5 w-1.5 flex-none" style={{ background: meta.color }} aria-hidden />
              <span className="font-display text-[11px] font-bold tracking-wide" style={{ color: meta.color }}>{meta.en}</span>
              <span className="ml-auto font-display text-[10px] tabular-nums" style={{ color: meta.color }}>{budgetTotal > 0 ? `${budgetTotal >= 1e8 ? eok(budgetTotal) : '₩' + man(budgetTotal)} · VAT 별도` : '예산 미정'}</span>
            </div>
            <div className="space-y-1.5">
              {items.length === 0 ? (
                <div className="border border-dashed border-line-hairline bg-paper py-3 text-center text-[11px] text-fg-3">—</div>
              ) : (
                items.map((c) => (
                  <button key={c.id} onClick={() => onOpen(c.id)} className="flex w-full items-start gap-2.5 border border-line-hairline bg-surface-base px-2.5 py-2 text-left hover:bg-surface-soft">
                    <span className="mt-px flex-none font-display text-sm font-bold tabular-nums" style={{ color: meta.color }}>{String(c.progress).padStart(2, '0')}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[12px] font-bold text-fg-1">{c.brand}</div>
                      <div className="truncate text-[10px] text-fg-3">{c.title}</div>
                      {c.budget != null && c.progress >= 3 && (
                        <div className="mt-0.5 font-display text-[10px] font-semibold tabular-nums text-fg-1">{won(c.budget)}<span className="ml-1 font-sans font-normal text-fg-3">· VAT 별도</span></div>
                      )}
                    </div>
                    <span className="mt-px flex-none font-display text-xs font-bold tabular-nums text-fg-2">{c.progress}/8</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )
      })}
      </div>
    </div>
  )
}

function BoardView({ board, period, onOpen, onDeleteDraft }: { board: CampaignBoard; period: Period; onOpen: (id: string) => void; onDeleteDraft: (id: string) => void }) {
  const [mode, setMode] = useState<BoardMode>('kanban')
  const cs = board.campaigns
  const byStage = (s: CampaignStage) => cs.filter((c) => c.stage === s)
  const refDate = new Date().toISOString().slice(0, 10) // 경과일(D+n) 기준 = 오늘
  const k = board.kpi
  // 'all'은 BE 집계(정확)를 그대로 쓰고, 기간 선택 시엔 필터된 목록에서 파생 가능한 값만 재계산.
  // (이번주 마감·업로드 예정·GMV는 기간 단위로 신뢰성 있게 파생 불가 → 기간 뷰에선 파이프라인 카운트로 대체)
  const inProg = OPEN_STAGES.reduce((n, s) => n + byStage(s).length, 0)
  const published = byStage('live').length + byStage('complete').length
  const viewsSum = cs.reduce((n, c) => n + c.channels.reduce((m, ch) => m + (ch.views ?? 0), 0), 0)
  const revenueSum = cs.reduce((n, c) => n + (c.budget ?? 0), 0) // 기간 내(openedDate) 캠페인 매출 합
  const cards =
    period === 'all'
      ? [
          { label: '진행중 캠페인', value: String(k.inProgress) },
          { label: '이번주 마감', value: String(k.dueThisWeek) },
          { label: '업로드 예정', value: String(k.toUpload) },
          { label: '누적 조회', value: man(k.cumulativeViews) },
          { label: '전체 매출', value: eok(k.totalRevenue) },
        ]
      : [
          { label: '캠페인', value: String(cs.length) },
          { label: '진행중', value: String(inProg) },
          { label: '발행·마감', value: String(published) },
          { label: '조회 합계', value: viewsSum > 0 ? man(viewsSum) : '—' },
          { label: '전체 매출', value: revenueSum > 0 ? eok(revenueSum) : '—' },
        ]
  return (
    <div className="space-y-6">
      {/* KPI 리본 — 이어진 스트립(옅은 구분선) */}
      <div className="grid grid-cols-2 gap-px overflow-hidden border border-[rgba(53,38,40,0.05)] bg-[rgba(53,38,40,0.05)] sm:grid-cols-3 lg:grid-cols-5">
        {cards.map((c) => (
          <KpiCard key={c.label} label={c.label} value={c.value} />
        ))}
      </div>

      {/* 파이프라인 헤더 + 모드 토글 */}
      <div className="flex items-center gap-4">
        <div className="flex-none text-[11px] font-semibold uppercase tracking-widest text-fg-3">
          Pipeline · 파이프라인
        </div>
        <div className="h-px flex-1 bg-line-hairline" aria-hidden />
        <div className="flex flex-none items-center border border-line-hairline bg-surface-base">
          {BOARD_MODES.map((m) => (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              className={'px-3 py-1.5 text-xs font-medium ' + (mode === m.key ? 'bg-ink-800 text-fg-inverse' : 'text-fg-3 hover:text-fg-1')}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* 본문 (모드별) */}
      {mode === 'list' ? (
        <BoardList cs={cs} onOpen={onOpen} />
      ) : mode === 'compact' ? (
        <BoardCompact byStage={byStage} onOpen={onOpen} />
      ) : (
      <div className="-mx-8 overflow-x-auto px-8 pb-1">
      <div className="grid grid-cols-[repeat(5,minmax(200px,1fr))] gap-3">
        {CAMPAIGN_STAGES.map((stage) => {
          const items = byStage(stage)
          const meta = PHASE[stage]
          const budgetTotal = items.reduce((n, c) => n + (c.budget ?? 0), 0)
          return (
            <div key={stage} className="min-w-0">
              <div className="mb-2.5 flex items-start justify-between gap-2 border-b-2 pb-2" style={{ borderColor: meta.color }}>
                <div className="flex min-w-0 items-start gap-2">
                  <span className="mt-[3px] h-[9px] w-[9px] flex-none" style={{ background: meta.color }} aria-hidden />
                  <div className="min-w-0">
                    <div className="font-display text-[13px] font-bold tracking-[0.05em] text-fg-1">{meta.en}</div>
                    <div className="mt-0.5 text-[10px] text-fg-3">{meta.kr}</div>
                  </div>
                </div>
                <div className="flex-none text-right">
                  <div className="font-display text-xl font-bold leading-none tabular-nums text-fg-1">{items.length}</div>
                  <div className="mt-1 text-[10px] font-semibold font-display tabular-nums text-fg-3">{budgetTotal > 0 ? `${budgetTotal >= 1e8 ? eok(budgetTotal) : '₩' + man(budgetTotal)} · VAT 별도` : '예산 미정'}</div>
                </div>
              </div>
              <div className="space-y-2.5">
                {items.length === 0 ? (
                  <div className="border border-dashed border-line-hairline bg-paper py-6 text-center text-[11px] text-fg-3">—</div>
                ) : (
                  items.map((c) => <CampaignCard key={c.id} c={c} refDate={refDate} onOpen={() => onOpen(c.id)} onDelete={c.isDraft ? () => onDeleteDraft(c.id) : undefined} />)
                )}
              </div>
            </div>
          )
        })}
      </div>
      </div>
      )}
      <div className="text-[11px] text-fg-3">
        전체 {board.campaigns.length}건 (진행 {OPEN_STAGES.reduce((n, s) => n + byStage(s).length, 0)} · 정산마감 {byStage('complete').length}) · 모든 금액 VAT 별도
      </div>
    </div>
  )
}

// ── Calendar ───────────────────────────────────────────
function CalendarView({ events }: { events: CampaignScheduleEvent[] }) {
  const now = new Date()
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 })
  // 진입 시 오늘 날짜 자동 선택(현재 달 기준)
  const [selDay, setSelDay] = useState<number | null>(now.getDate())
  const key = `${ym.y}-${String(ym.m).padStart(2, '0')}`
  // 월 → 일별 이벤트 맵 (SF 일정 실데이터)
  const monthEv = useMemo(() => {
    const map: Record<number, CalEvent[]> = {}
    for (const e of events) {
      if (!e.date.startsWith(key)) continue
      const day = Number(e.date.slice(8, 10))
      ;(map[day] ??= []).push({ ev: e.label, camp: e.brand, tag: e.tag })
    }
    return map
  }, [events, key])
  const lead = new Date(ym.y, ym.m - 1, 1).getDay()
  const daysInMonth = new Date(ym.y, ym.m, 0).getDate()
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1)
  const move = (delta: number) => {
    const d = new Date(ym.y, ym.m - 1 + delta, 1)
    const y = d.getFullYear()
    const m = d.getMonth() + 1
    setYm({ y, m })
    // 현재 달로 이동하면 오늘 자동 선택, 아니면 해제
    setSelDay(y === now.getFullYear() && m === now.getMonth() + 1 ? now.getDate() : null)
  }
  const selEvents = selDay != null ? (monthEv[selDay] ?? []) : []
  const dueSoon = Object.entries(monthEv)
    .flatMap(([d, evs]) => evs.filter((e) => e.tag === '마감').map((e) => ({ day: Number(d), ...e })))
    .sort((a, b) => a.day - b.day)
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.8fr_1fr]">
      {/* 월 그리드 */}
      <div className="border border-line-hairline bg-surface-base px-5 py-4">
        <div className="mb-3 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <button onClick={() => move(-1)} aria-label="이전 달" className="flex h-6 w-6 items-center justify-center text-fg-3 hover:text-fg-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue">
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </button>
              <span className="font-display text-xl font-bold tabular-nums text-fg-1">{ym.y} · {MONTH_EN[ym.m - 1]}</span>
              <button onClick={() => move(1)} aria-label="다음 달" className="flex h-6 w-6 items-center justify-center text-fg-3 hover:text-fg-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue">
                <ChevronRight className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <div className="mt-0.5 pl-1 text-[11px] text-fg-3">일 단위 일정 · 마감</div>
          </div>
          {/* 범례 */}
          <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1.5 pt-1">
            {[
              { t: '촬영·편집', c: '#3E3BE0' },
              { t: '업로드', c: '#5653FA' },
              { t: '라이브', c: '#C84BC9' },
              { t: '마감 Due', c: '#B4453C' },
            ].map((l) => (
              <span key={l.t} className="inline-flex items-center gap-1.5 text-[10px] text-fg-3">
                <span className="h-2 w-2 flex-none" style={{ background: l.c }} aria-hidden />
                {l.t}
              </span>
            ))}
          </div>
        </div>
        <div className="overflow-hidden border border-line-hairline bg-paper">
          {/* 요일 헤더 (한글) */}
          <div className="grid grid-cols-7 border-b border-line-hairline bg-surface-soft">
            {WEEKDAYS_KR.map((w, i) => (
              <div key={i} className={'py-2 text-center text-[11px] font-semibold ' + (i === 0 ? 'text-status-danger' : i === 6 ? 'text-brand-blue' : 'text-fg-2')}>{w}</div>
            ))}
          </div>
          {/* 날짜 그리드 (연결된 테이블) */}
          <div className="grid grid-cols-7">
            {Array.from({ length: lead }).map((_, i) => <div key={'lead' + i} className="border-b border-r border-line-hairline bg-surface-soft/40" />)}
            {days.map((d) => {
              const evs = monthEv[d] ?? []
              const dow = (d - 1 + lead) % 7
              const sel = d === selDay
              const isToday = ym.y === now.getFullYear() && ym.m === now.getMonth() + 1 && d === now.getDate()
              return (
                <button
                  key={d}
                  onClick={() => setSelDay(d)}
                  className={'flex h-[108px] flex-col overflow-hidden border-b border-r border-line-hairline p-1.5 text-left align-top transition ' + (sel ? 'bg-[rgba(86,83,250,0.06)] ring-1 ring-inset ring-brand-blue' : 'hover:bg-surface-soft')}
                >
                  <span className={isToday ? 'flex h-6 w-6 items-center justify-center rounded-full bg-brand-blue text-[11px] font-semibold font-display tabular-nums text-fg-inverse' : 'text-[12px] font-medium font-display tabular-nums ' + (dow === 0 ? 'text-status-danger' : dow === 6 ? 'text-brand-blue' : 'text-fg-1')}>{d}</span>
                  <div className="mt-1 space-y-0.5">
                    {evs.slice(0, 3).map((e, i) => <TagChip key={i} tag={e.tag}>{e.camp} {e.ev}</TagChip>)}
                    {evs.length > 3 && <div className="pl-1 text-[9px] font-medium text-fg-3">+{evs.length - 3}건 더</div>}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </div>
      {/* 우측: 선택일 + 마감 임박 */}
      <div className="space-y-5">
        <div className="border border-line-hairline bg-surface-base">
          {/* 선택일 헤더 — 라벤더 틴트 */}
          <div className="px-5 py-3" style={{ background: 'rgba(86,83,250,0.06)' }}>
            {selDay != null ? (
              <div className="flex items-baseline gap-2">
                <span className="font-display text-lg font-bold tabular-nums text-fg-1">{ym.m}월 {selDay}일</span>
                <span className="text-[11px] text-fg-3">{WEEKDAYS_KR[new Date(ym.y, ym.m - 1, selDay).getDay()]}요일 · {selEvents.length}개 일정</span>
              </div>
            ) : (
              <span className="text-[13px] font-medium text-fg-3">날짜를 선택하세요</span>
            )}
          </div>
          {selDay != null && (
            <div className="px-5 py-4">
              {selEvents.length === 0 ? (
                <div className="py-2 text-center text-[12px] text-fg-3">일정 없음</div>
              ) : (
                <ul className="space-y-2">
                  {selEvents.map((e, i) => (
                    <li key={i} className="flex items-center gap-2 text-[12px]">
                      <span className="h-2 w-2 flex-none" style={{ background: EC[e.tag] ?? '#5653FA' }} aria-hidden />
                      <span className="font-medium text-fg-1">{e.ev}</span>
                      <span className="ml-auto text-fg-3">{e.camp}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="border border-line-hairline bg-surface-base px-5 py-4">
          <div className="mb-2.5 text-[10px] font-semibold uppercase tracking-wider text-fg-3">Upcoming · 마감 임박</div>
          {dueSoon.length === 0 ? (
            <div className="py-4 text-center text-[12px] text-fg-3">마감 예정 없음</div>
          ) : (
            <ul className="space-y-2">
              {dueSoon.map((e, i) => (
                <li key={i} className="flex items-center gap-2.5 text-[12px]">
                  <span className="w-10 flex-none font-display tabular-nums text-status-danger">{ym.m}.{e.day}</span>
                  <span className="truncate font-medium text-fg-1">{e.camp} {e.ev}</span>
                  <span className="ml-auto flex-none px-1.5 py-0.5 text-[9px] font-semibold" style={{ background: '#B4453C1A', color: '#B4453C' }}>{e.tag}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

// ── History ────────────────────────────────────────────
function HistoryView({ board, onOpen }: { board: CampaignBoard; onOpen: (id: string) => void }) {
  const [brand, setBrand] = useState('전체')
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const done = board.campaigns.filter((c) => c.stage === 'live' || c.stage === 'complete')
  // 브랜드별 협업 수 → 상위 5개 고정 노출
  const brandCount = new Map<string, number>()
  done.forEach((c) => brandCount.set(c.brand, (brandCount.get(c.brand) ?? 0) + 1))
  const rankedBrands = Array.from(brandCount.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'ko'))
  const topBrands = rankedBrands.slice(0, 5)
  const allBrands = rankedBrands.map(([b]) => b).sort((a, b) => a.localeCompare(b, 'ko'))
  const shown = brand === '전체' ? done : done.filter((c) => c.brand === brand)
  const opts = ['전체', ...allBrands].filter((b) => b === '전체' || b.toLowerCase().includes(q.trim().toLowerCase()))
  return (
    <div className="space-y-4">
      {/* 브랜드 필터 — 좌측 협업 상위 5개 고정 칩 + 우측 검색 */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => { setBrand('전체'); setQ('') }}
          className={'inline-flex items-center border px-3 py-1.5 text-xs font-semibold transition ' + (brand === '전체' ? 'border-brand-blue bg-brand-blue text-fg-inverse' : 'border-line-hairline text-fg-2 hover:border-brand-blue hover:text-brand-blue')}
        >
          전체
        </button>
        {topBrands.map(([b, n]) => {
          const on = brand === b
          return (
            <button
              key={b}
              onClick={() => { setBrand(b); setQ('') }}
              className={'inline-flex items-center gap-1.5 border px-3 py-1.5 text-xs font-semibold transition ' + (on ? 'border-brand-blue bg-brand-blue text-fg-inverse' : 'border-line-hairline text-fg-2 hover:border-brand-blue hover:text-brand-blue')}
            >
              <span className="max-w-[8rem] truncate">{b}</span>
              <span className={'font-display tabular-nums text-[10px] ' + (on ? 'text-fg-inverse/80' : 'text-fg-3')}>{n}</span>
            </button>
          )
        })}
        <div className="relative ml-auto w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-3" aria-hidden />
          <input
            value={open ? q : brand === '전체' ? '' : brand}
            onChange={(e) => {
              setQ(e.target.value)
              setOpen(true)
            }}
            onFocus={() => {
              setOpen(true)
              setQ('')
            }}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder="브랜드·캠페인 검색"
            className="h-9 w-full border border-line-hairline bg-surface-base pl-8 pr-8 text-sm text-fg-1 placeholder:text-fg-3 focus:border-brand-blue focus:outline-none"
          />
          {brand !== '전체' && !open && (
            <button
              onClick={() => {
                setBrand('전체')
                setQ('')
              }}
              aria-label="필터 해제"
              className="absolute right-2 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center text-fg-3 hover:text-fg-1"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {open && (
            <div className="absolute left-0 top-full z-20 mt-1 max-h-64 w-full overflow-y-auto border border-line-hairline bg-surface-base shadow-lg">
              {opts.length === 0 ? (
                <div className="px-3 py-2 text-xs text-fg-3">결과 없음</div>
              ) : (
                opts.map((b) => (
                  <button
                    key={b}
                    onMouseDown={() => {
                      setBrand(b)
                      setQ('')
                      setOpen(false)
                    }}
                    className={'block w-full px-3 py-1.5 text-left text-sm hover:bg-surface-soft ' + (b === brand ? 'font-semibold text-brand-blue' : 'text-fg-2')}
                  >
                    {b}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>
      <div className="text-[11px] text-fg-3">{shown.length}개 캠페인 · 하나의 캠페인에 여러 콘텐츠가 융합됩니다</div>

      {/* 정제 카드 (핸드오프 톤: 점 액센트·Grato 브랜드·융합 핀) */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {shown.map((c) => {
          const color = PHASE[c.stage].color
          return (
            <button key={c.id} onClick={() => onOpen(c.id)} className="block border border-line-hairline bg-surface-base p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue">
              {/* 헤더 — 점 + 브랜드 + 융합 핀 */}
              <div className="flex items-center gap-1.5">
                <span className="mr-0.5 h-2 w-2 flex-none" style={{ background: color }} aria-hidden />
                <span className="font-display text-[15px] font-bold text-fg-1">{c.brand}</span>
                {c.isLmpa && <LmpaTag />}
                {c.commerce && <span className="bg-[rgba(86,83,250,0.1)] px-1.5 py-0.5 text-[9px] font-semibold text-brand-blue">커머스</span>}
                <span className="ml-auto flex-none px-2 py-0.5 text-[10px] font-semibold" style={{ color, background: color + '1A' }}>{c.channels.length}개 콘텐츠 융합</span>
              </div>
              <div className="mt-2 text-sm font-bold leading-snug text-fg-1">{c.title}</div>
              {c.product && <div className="mt-0.5 truncate text-[11px] text-fg-3">{c.product}</div>}
              {/* 융합 콘텐츠 칩 (썸네일·지표는 분석 연동 후) */}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {c.channels.map((ch, i) => {
                  const pill = CH_PILL[ch.platform]
                  return (
                    <span key={i} className="inline-flex items-center gap-1 border border-line-hairline py-1 pl-1 pr-2 text-[10px]">
                      <span className="px-1 py-0.5 text-[9px] font-semibold" style={{ background: pill.bg, color: pill.color }}>{pill.label}</span>
                      <span className="text-fg-2">{ch.contentType}</span>
                    </span>
                  )
                })}
              </div>
              {/* 메타 — 보유 지표만 */}
              <div className="mt-4 grid grid-cols-3 gap-3 border-t border-line-hairline pt-3 text-[11px]">
                <div className="min-w-0">
                  <div className="text-fg-3">기간</div>
                  <div className="mt-0.5 font-display tabular-nums font-semibold text-fg-1">{periodLabel(c)}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-fg-3">예산</div>
                  <div className="mt-0.5 whitespace-nowrap font-display font-semibold tabular-nums text-fg-1">{c.budget != null ? won(c.budget) : '—'}</div>
                  {c.budget != null && <div className="text-[9px] font-normal text-fg-3">VAT 별도</div>}
                </div>
                <div className="min-w-0">
                  <div className="text-fg-3">{c.commerce ? '커머스' : '채널'}</div>
                  <div className="mt-0.5 break-keep font-semibold text-fg-1">{c.commerce ? c.commerce.metric : `${c.channels.length}개 융합`}</div>
                </div>
              </div>
            </button>
          )
        })}
      </div>
      {shown.length === 0 && <div className="py-10 text-center text-sm text-fg-3">발행 완료된 캠페인이 없습니다.</div>}
    </div>
  )
}

// ── New ────────────────────────────────────────────────
type NewRow = { platform: CampaignChannel['platform']; contentType: string; url: string }
function NewCampaignModal({ onCancel, onCreate }: { onCancel: () => void; onCreate: (input: NewCampaignInput) => void | Promise<void> }) {
  const [brand, setBrand] = useState('')
  const [title, setTitle] = useState('')
  const [owner, setOwner] = useState('')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [budget, setBudget] = useState('')
  const [startStage, setStartStage] = useState<CampaignStage>('intake')
  const [commerce, setCommerce] = useState(false)
  const [rows, setRows] = useState<NewRow[]>([{ platform: 'youtube', contentType: '', url: '' }])
  const [saving, setSaving] = useState(false)
  const setRow = (i: number, f: keyof NewRow, v: string) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [f]: v } : r)))
  const canCreate = !!(brand.trim() && title.trim()) && !saving
  const submit = async () => {
    if (!canCreate) return
    setSaving(true)
    const budgetNum = Number(budget.replace(/[^0-9]/g, ''))
    const input: NewCampaignInput = {
      brand: brand.trim(),
      title: title.trim(),
      owner: owner.trim(),
      periodStart: start.trim(),
      periodEnd: end.trim(),
      budget: budgetNum > 0 ? budgetNum : undefined,
      startStage,
      commerceFusion: commerce,
      channels: rows
        .filter((r) => r.contentType.trim() || r.url.trim())
        .map((r) => ({ platform: r.platform, contentType: r.contentType.trim() || CH_PILL[r.platform].label, url: r.url.trim() || undefined })),
    }
    try {
      await onCreate(input)
    } finally {
      setSaving(false)
    }
  }

  const field = 'w-full border border-line-hairline bg-surface-base px-3 py-2 text-sm text-fg-1 focus:border-brand-blue focus:outline-none'
  const lab = 'text-[11px] font-semibold text-fg-3'
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-[color:var(--bg-overlay)]" onClick={onCancel} aria-hidden />
      <div className="relative flex max-h-[88vh] w-full max-w-2xl flex-col overflow-y-auto border border-line-soft bg-surface-base shadow-xl">
        <div className="flex items-center justify-between border-b border-line-soft px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="text-lg font-bold text-fg-1">신규 캠페인 등록</div>
            <span className="border border-line-soft px-1.5 py-0.5 text-[10px] font-semibold text-fg-3">내부 드래프트</span>
          </div>
          <button onClick={onCancel} className="flex h-8 w-8 items-center justify-center text-fg-3 hover:text-fg-1" aria-label="닫기"><X className="h-4 w-4" aria-hidden /></button>
        </div>
        <div className="space-y-5 px-5 py-5">
          {/* Salesforce 미연동 안내 */}
          <div className="flex items-start gap-2 border-l-[3px] border-brand-blue bg-[rgba(86,83,250,0.06)] px-3 py-2.5">
            <Info className="mt-0.5 h-4 w-4 flex-none text-brand-blue" aria-hidden />
            <div className="text-[12px] leading-relaxed text-fg-2">
              <b className="font-semibold text-fg-1">Salesforce에 연동되지 않는 내부 드래프트</b>입니다. 실제 기회(Opportunity)는 영업팀이 Salesforce에서 생성·관리해요.
            </div>
          </div>
      {/* 기본 정보 */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="space-y-1"><div className={lab}>브랜드 *</div><input className={field} value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="예: espoir" /></label>
        <label className="space-y-1"><div className={lab}>캠페인명 *</div><input className={field} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="예: 2026 S/S 앰버서더" /></label>
        <label className="space-y-1"><div className={lab}>담당자</div><input className={field} value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="예: 최인석" /></label>
        <label className="space-y-1"><div className={lab}>예산 (원)</div><input className={field} value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="예: 42000000" inputMode="numeric" /></label>
        <label className="space-y-1"><div className={lab}>시작 (YYYY-MM)</div><input className={field} value={start} onChange={(e) => setStart(e.target.value)} placeholder="2026-02" /></label>
        <label className="space-y-1"><div className={lab}>종료 (YYYY-MM)</div><input className={field} value={end} onChange={(e) => setEnd(e.target.value)} placeholder="2026-04" /></label>
        <label className="space-y-1"><div className={lab}>시작 단계</div>
          <select className={field} value={startStage} onChange={(e) => setStartStage(e.target.value as CampaignStage)}>
            {CAMPAIGN_STAGES.map((s) => <option key={s} value={s}>{CAMPAIGN_STAGE_LABELS[s]}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 pt-6 text-sm text-fg-2">
          <input type="checkbox" checked={commerce} onChange={(e) => setCommerce(e.target.checked)} /> 커머스 융합
        </label>
      </div>
      {/* 채널 */}
      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <div className={lab}>채널 구성</div>
          <button onClick={() => setRows((rs) => [...rs, { platform: 'instagram', contentType: '', url: '' }])} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-blue">
            <Plus className="h-3.5 w-3.5" aria-hidden /> 채널 추가
          </button>
        </div>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[110px_1fr_1.4fr_28px] items-center gap-2">
              <select className={field} value={r.platform} onChange={(e) => setRow(i, 'platform', e.target.value)}>
                {(['youtube', 'instagram', 'tiktok', 'commerce', 'editorial'] as CampaignChannel['platform'][]).map((p) => <option key={p} value={p}>{CH_PILL[p].label}</option>)}
              </select>
              <input className={field} value={r.contentType} onChange={(e) => setRow(i, 'contentType', e.target.value)} placeholder="콘텐츠 유형" />
              <input className={field} value={r.url} onChange={(e) => setRow(i, 'url', e.target.value)} placeholder="URL (선택)" />
              <button onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))} className="flex h-8 w-7 items-center justify-center text-fg-3 hover:text-fg-1" aria-label="채널 삭제"><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-line-soft bg-surface-soft px-5 py-4">
          <span className="text-[11px] text-fg-3">내부 드래프트로 저장 · Salesforce 미연동 (저장 연동 후속)</span>
          <div className="flex gap-2">
            <button onClick={onCancel} className="h-10 border border-line-soft px-4 text-sm font-medium text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none">취소</button>
            <button onClick={submit} disabled={!canCreate} className="h-10 bg-brand-blue px-5 text-sm font-semibold text-fg-inverse disabled:opacity-40 focus-visible:outline-none">{saving ? '저장 중…' : '드래프트 저장'}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Detail ─────────────────────────────────────────────
function MetaTile({ label, value, sub, valueColor }: { label: string; value: string; sub?: string; valueColor?: string }) {
  return (
    <div className="bg-surface-base px-3.5 py-3">
      <div className="text-[10px] tracking-wide text-fg-3">{label}</div>
      <div className="mt-0.5 font-display text-[15px] font-bold tabular-nums" style={{ color: valueColor ?? 'var(--ink-800)' }}>{value}</div>
      {sub && <div className="text-[9px] text-fg-3">{sub}</div>}
    </div>
  )
}

function CampaignDetail({ c, onBack, onChanged }: { c: Campaign; onBack: () => void; onChanged?: () => void }) {
  const color = PHASE[c.stage].color
  const progressPct = Math.round((c.progress / 8) * 100)

  // 앱-상태(진척) 변경 — SF 미세분 구간(제작~라이브)을 앱에서 진행. 드래프트·정산 완료는 편집 불가.
  const editable = !c.isDraft && c.progress < 8
  const [savingState, setSavingState] = useState(false)
  const changeStep = async (i: number) => {
    if (!editable || savingState) return
    setSavingState(true)
    await setCampaignState(CREATOR_ID, { campaignKey: c.id, progress: STEP_TO_PROG[i] }).catch(() => {})
    setSavingState(false)
    onChanged?.()
  }
  const resetState = async () => {
    if (savingState) return
    setSavingState(true)
    await clearCampaignState(CREATOR_ID, c.id).catch(() => {})
    setSavingState(false)
    onChanged?.()
  }

  // 발행 콘텐츠 링크 (지표 매칭은 후속)
  const [contents, setContents] = useState<CampaignContent[]>([])
  const [addPlatform, setAddPlatform] = useState<CampaignChannel['platform']>('youtube')
  const [addType, setAddType] = useState('')
  const [addUrl, setAddUrl] = useState('')
  const [savingContent, setSavingContent] = useState(false)
  const refreshContents = () =>
    fetchCampaignContents(CREATOR_ID)
      .then((list) => setContents(list.filter((x) => x.campaignKey === c.id)))
      .catch(() => setContents([]))
  useEffect(() => { refreshContents() }, [c.id])

  // 영상 지표(조회수·시청률·전환) — 포털 Mongo(YouTube OAuth). cic 는 이 값을 못 낸다.
  //   채널 표와 발행 콘텐츠 표의 URL 을 한 번에 조회해 videoId 로 병합한다.
  //   유튜브가 아니거나 미연동이면 키가 없어 그 행은 "—" 그대로다.
  const [videoMetrics, setVideoMetrics] = useState<Record<string, VideoMetric>>({})
  useEffect(() => {
    const urls = [
      ...c.channels.map((ch) => ch.url),
      ...contents.map((ct) => ct.url),
    ].filter((u): u is string => !!u && !!youtubeVideoId(u))
    if (urls.length === 0) {
      setVideoMetrics({})
      return
    }
    let alive = true
    fetchVideoMetrics(urls).then((m) => {
      if (alive) setVideoMetrics(m)
    })
    return () => {
      alive = false
    }
  }, [c.id, c.channels, contents])
  /** URL → 지표. 없으면 null(행은 "—"). */
  const metricOf = (url: string | null | undefined): VideoMetric | null => {
    const id = youtubeVideoId(url)
    return id ? (videoMetrics[id] ?? null) : null
  }
  const addContent = async () => {
    if (!addUrl.trim() || savingContent) return
    setSavingContent(true)
    await addCampaignContent(CREATOR_ID, { campaignKey: c.id, platform: addPlatform, contentType: addType.trim() || undefined, url: addUrl.trim() }).catch(() => null)
    setAddType('')
    setAddUrl('')
    await refreshContents()
    setSavingContent(false)
  }
  const removeContent = async (id: string) => {
    await deleteCampaignContent(CREATOR_ID, id).catch(() => {})
    await refreshContents()
  }

  return (
    <div className="space-y-5">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-xs font-semibold text-fg-3 hover:text-fg-1">
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> 파이프라인으로
      </button>
      <div className="relative overflow-hidden border border-line-hairline bg-surface-soft p-6">
        <div className="h-1 w-10" style={{ background: color }} aria-hidden />
        <div className="mt-3 flex items-center gap-1.5">
          {c.isLmpa && <span className="bg-brand-blue px-1.5 py-0.5 text-[9px] font-bold text-fg-inverse">LMPA</span>}
          <span className="text-xs font-semibold" style={{ color: c.isLmpa ? '#5653FA' : undefined }}>{c.brand}</span>
          {c.commerce && <span className="bg-[rgba(86,83,250,0.1)] px-1.5 py-0.5 text-[9px] font-semibold text-brand-blue">커머스</span>}
          <span className="ml-auto inline-flex items-center gap-1.5 px-2 py-0.5 font-display text-[10px] font-semibold" style={{ background: color + '1A', color }}>
            {String(c.progress).padStart(2, '0')} · {microLabel(c.progress)}
          </span>
        </div>
        <h2 className="mt-2 font-display text-2xl font-bold text-fg-1">{c.title}</h2>
        {c.product && <div className="mt-1 text-sm text-fg-2">{c.product}</div>}
        <div className="mt-3 flex items-center gap-2 text-[11px] text-fg-3">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ink-800 text-[10px] font-semibold text-fg-inverse">{c.owner.slice(0, 1)}</span>
          담당 {c.owner}
        </div>
      </div>
      {/* 6-스텝 진척 인디케이터 — 앱에서 단계 진행(클릭) 관리 */}
      <div className="border border-line-hairline bg-surface-base px-5 py-4">
        <div className="mb-3 flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-fg-3">진행 단계</span>
          {editable && <span className="text-[11px] text-fg-3">· 단계를 눌러 진행 상태를 바꿉니다</span>}
          {editable && (
            <button onClick={resetState} disabled={savingState} className="ml-auto text-[11px] font-medium text-fg-3 underline-offset-2 hover:text-fg-1 hover:underline disabled:opacity-40">
              SF 자동으로 복원
            </button>
          )}
        </div>
        <div className="overflow-x-auto py-2">
          <div className="flex min-w-[520px] items-start">
            {STEP6.map((label, i) => {
              const cur = stepOf(c.progress)
              const on = i <= cur
              const current = i === cur
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => changeStep(i)}
                  disabled={!editable || savingState}
                  className={'group flex flex-1 flex-col items-center bg-transparent ' + (editable ? 'cursor-pointer disabled:cursor-wait' : 'cursor-default')}
                >
                  <div className="flex w-full items-center">
                    <div className={'h-0.5 flex-1 ' + (i === 0 ? 'opacity-0' : '')} style={{ background: i <= cur ? color : 'var(--ink-200)' }} />
                    <div className={'h-3.5 w-3.5 flex-none rounded-full border-2 transition ' + (editable ? 'group-hover:scale-125' : '')} style={{ background: on ? color : 'var(--paper)', borderColor: on ? color : 'var(--ink-200)', boxShadow: current ? `0 0 0 3px ${color}33` : undefined }} />
                    <div className={'h-0.5 flex-1 ' + (i === STEP6.length - 1 ? 'opacity-0' : '')} style={{ background: i < cur ? color : 'var(--ink-200)' }} />
                  </div>
                  <div className={'mt-2 text-center text-[10px] leading-tight ' + (editable ? 'group-hover:text-brand-blue' : '')} style={{ color: on ? 'var(--ink-800)' : 'var(--fg-3)', fontWeight: current ? 700 : 500 }}>
                    {label}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
        {!editable && (
          <div className="mt-3 text-[11px] text-fg-3">{c.isDraft ? '내부 드래프트는 단계 관리를 지원하지 않습니다.' : 'SF 정산 완료 건은 단계가 고정됩니다.'}</div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-px border border-line-hairline bg-line-hairline sm:grid-cols-4">
        <MetaTile label="기간" value={periodLabel(c)} />
        <MetaTile label="예산" value={c.budget != null ? won(c.budget) : '미정'} sub={c.budget != null ? 'VAT 별도' : undefined} />
        <MetaTile label="채널" value={`${c.channels.length}개`} />
        <MetaTile label="진행률" value={`${progressPct}%`} valueColor={color} />
      </div>
      <div>
        <div className="mb-2.5 flex items-center gap-2">
          <span className="h-1.5 w-1.5 bg-brand-blue" aria-hidden />
          <span className="font-display text-[15px] font-bold text-fg-1">채널 & 콘텐츠</span>
        </div>
        <div className="overflow-x-auto border border-line-hairline bg-surface-base">
          <div className="min-w-[640px]">
            <div className="grid grid-cols-[110px_1.2fr_1.6fr_0.8fr_0.8fr_0.8fr] border-b border-line-hairline bg-surface-soft text-[10px] font-semibold tracking-wide text-fg-3">
              <div className="px-3 py-2.5">플랫폼</div><div className="px-3 py-2.5">콘텐츠 유형</div><div className="px-3 py-2.5">URL</div>
              <div className="px-3 py-2.5 text-right">조회수</div><div className="px-3 py-2.5 text-right">시청률</div><div className="px-3 py-2.5 text-right">전환</div>
            </div>
            {c.channels.map((ch, i) => {
              const pill = CH_PILL[ch.platform]
              return (
                <div key={i} className="grid grid-cols-[110px_1.2fr_1.6fr_0.8fr_0.8fr_0.8fr] items-center border-b border-line-hairline last:border-b-0">
                  <div className="px-3 py-3"><span className="px-2 py-0.5 text-[10px] font-semibold" style={{ background: pill.bg, color: pill.color }}>{pill.label}</span></div>
                  <div className="px-3 py-3 text-xs text-fg-1">{ch.contentType}</div>
                  <div className="truncate px-3 py-3 text-xs text-fg-3">{ch.url ?? '—'}</div>
                  <div className="px-3 py-3 text-right text-xs font-display tabular-nums text-fg-1">{(ch.views ?? metricOf(ch.url)?.views) != null ? man((ch.views ?? metricOf(ch.url)!.views)!) : '—'}</div>
                  <div className="px-3 py-3 text-right text-xs font-display tabular-nums text-fg-2">{ch.watchRate != null ? pct(ch.watchRate) : metricOf(ch.url)?.watchRate != null ? pctPoint(metricOf(ch.url)!.watchRate!) : '—'}</div>
                  {/* 전환 = 구독전환(순증). % 가 아니라 명 수라 signed 정수로 적는다 */}
                  <div className="px-3 py-3 text-right text-xs font-display tabular-nums text-fg-2">{metricOf(ch.url)?.netSubs != null ? signedInt(metricOf(ch.url)!.netSubs!) : ch.conversion != null ? pct(ch.conversion) : '—'}</div>
                </div>
              )
            })}
          </div>
        </div>
        <div className="mt-1.5 text-[10px] text-fg-3">조회수·시청률·전환은 YouTube 연동 채널의 발행 영상에서 집계 · 미연동·타 플랫폼·발행 전은 —</div>
      </div>

      {/* 발행 콘텐츠 & 지표 — 캠페인에 실제 발행 링크 첨부 */}
      <div>
        <div className="mb-2.5 flex items-center gap-2">
          <span className="h-1.5 w-1.5 bg-brand-blue" aria-hidden />
          <span className="font-display text-[15px] font-bold text-fg-1">발행 콘텐츠 & 지표</span>
          <span className="text-[11px] text-fg-3">· 발행된 콘텐츠 링크를 연결하면 지표가 표시됩니다</span>
        </div>
        {/* 추가 폼 */}
        <div className="mb-2 grid grid-cols-[110px_1fr_1.6fr_64px] items-center gap-2">
          <FieldSelect
            ariaLabel="플랫폼"
            value={addPlatform}
            onChange={(v) => { setAddPlatform(v as CampaignChannel['platform']); setAddType('') }}
            options={(['youtube', 'instagram', 'tiktok', 'commerce', 'editorial'] as CampaignChannel['platform'][]).map((p) => ({ value: p, label: CH_PILL[p].label }))}
          />
          <FieldSelect
            ariaLabel="콘텐츠 유형"
            value={addType}
            onChange={setAddType}
            placeholder="콘텐츠 유형 선택"
            options={CONTENT_TYPES[addPlatform].map((t) => ({ value: t, label: t }))}
          />
          <input value={addUrl} onChange={(e) => setAddUrl(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addContent() }} placeholder="콘텐츠 URL 붙여넣기" className="h-9 border border-line-hairline bg-surface-base px-3 text-sm text-fg-1 placeholder:text-fg-3 focus:border-brand-blue focus:outline-none" />
          <button onClick={addContent} disabled={!addUrl.trim() || savingContent} className="inline-flex h-9 items-center justify-center gap-1 bg-brand-blue px-2 text-xs font-semibold text-fg-inverse disabled:opacity-40">
            <Plus className="h-3.5 w-3.5" aria-hidden /> 추가
          </button>
        </div>
        <div className="overflow-x-auto border border-line-hairline bg-surface-base">
          <div className="min-w-[640px]">
            <div className="grid grid-cols-[110px_1.2fr_1.8fr_0.7fr_0.7fr_0.7fr_36px] border-b border-line-hairline bg-surface-soft text-[10px] font-semibold tracking-wide text-fg-3">
              <div className="px-3 py-2.5">플랫폼</div><div className="px-3 py-2.5">콘텐츠 유형</div><div className="px-3 py-2.5">URL</div>
              <div className="px-3 py-2.5 text-right">조회수</div><div className="px-3 py-2.5 text-right">참여율</div><div className="px-3 py-2.5 text-right">전환</div><div className="px-3 py-2.5" />
            </div>
            {contents.length === 0 ? (
              <div className="px-3 py-6 text-center text-[11px] text-fg-3">연결된 발행 콘텐츠가 없습니다. URL을 추가해 보세요.</div>
            ) : (
              contents.map((ct) => {
                const pill = CH_PILL[ct.platform]
                return (
                  <div key={ct.id} className="grid grid-cols-[110px_1.2fr_1.8fr_0.7fr_0.7fr_0.7fr_36px] items-center border-b border-line-hairline last:border-b-0">
                    <div className="px-3 py-3"><span className="px-2 py-0.5 text-[10px] font-semibold" style={{ background: pill.bg, color: pill.color }}>{pill.label}</span></div>
                    <div className="px-3 py-3 text-xs text-fg-1">{ct.contentType || '—'}</div>
                    <div className="truncate px-3 py-3 text-xs">
                      <a href={ct.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-brand-blue hover:underline">{ct.url}</a>
                    </div>
                    <div className="px-3 py-3 text-right text-xs font-display tabular-nums text-fg-1">{(ct.views ?? metricOf(ct.url)?.views) != null ? man((ct.views ?? metricOf(ct.url)!.views)!) : '—'}</div>
                    <div className="px-3 py-3 text-right text-xs font-display tabular-nums text-fg-2">{ct.watchRate != null ? pct(ct.watchRate) : metricOf(ct.url)?.engagementRate != null ? pctPoint(metricOf(ct.url)!.engagementRate!) : '—'}</div>
                    <div className="px-3 py-3 text-right text-xs font-display tabular-nums text-fg-2">{metricOf(ct.url)?.netSubs != null ? signedInt(metricOf(ct.url)!.netSubs!) : ct.conversion != null ? pct(ct.conversion) : '—'}</div>
                    <div className="px-2 py-3 text-center">
                      <button onClick={() => removeContent(ct.id)} aria-label="콘텐츠 삭제" className="text-fg-3 hover:text-status-danger"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
        <div className="mt-1.5 text-[10px] text-fg-3">지표(조회수·참여율)는 콘텐츠 분석 데이터 매칭 후 자동 표시됩니다 · 전환은 커머스 연동 후속</div>
      </div>

      {c.commerce && (
        <div className="border border-[rgba(86,83,250,0.4)] bg-[rgba(86,83,250,0.05)] px-5 py-4">
          <div className="flex items-center gap-2">
            <span className="bg-brand-blue px-1.5 py-0.5 text-[9px] font-bold text-fg-inverse">커머스</span>
            <span className="text-sm font-semibold text-fg-1">{c.commerce.type}</span>
            <span className="ml-auto text-sm font-semibold text-brand-blue">{c.commerce.metric}</span>
          </div>
        </div>
      )}
    </div>
  )
}

// ── 섹션 ───────────────────────────────────────────────
type View = 'board' | 'calendar' | 'history'

export function CampaignsSection({ creatorId }: { creatorId?: string }) {
  if (creatorId) CREATOR_ID = creatorId // 셸 라우트 creator_id 반영(중첩 핸들러 공유)
  const [board, setBoard] = useState<CampaignBoard | null>(null)
  const [drafts, setDrafts] = useState<Campaign[]>([])
  const [view, setView] = useState<View>('board')
  const [period, setPeriod] = useState<Period>('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [newOpen, setNewOpen] = useState(false)
  const headerSlot = useContext(CreatorsHeaderSlotContext) // 셸 상단바 하단 슬롯

  const refreshDrafts = () => fetchCampaignDrafts(CREATOR_ID).then(setDrafts).catch(() => setDrafts([]))
  const refreshBoard = () => fetchCampaignBoard(CREATOR_ID).then(setBoard).catch(() => {})
  useEffect(() => {
    fetchCampaignBoard(CREATOR_ID).then(setBoard).catch(() => setBoard(null))
    refreshDrafts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorId])

  // 드래프트 + SF 캠페인 병합 (드래프트가 앞)
  const allCampaigns = useMemo(() => [...drafts, ...(board?.campaigns ?? [])], [drafts, board])
  const selected = useMemo(
    () => (selectedId ? allCampaigns.find((c) => c.id === selectedId) ?? null : null),
    [selectedId, allCampaigns],
  )

  if (!board) return <div className="p-8 text-sm text-fg-3">캠페인 불러오는 중…</div>

  const open = (id: string) => setSelectedId(id)

  const handleCreateDraft = async (input: NewCampaignInput) => {
    await createCampaignDraft(CREATOR_ID, input).catch(() => null)
    await refreshDrafts()
    setNewOpen(false)
  }
  const handleDeleteDraft = async (id: string) => {
    const n = Number(id.replace('draft:', ''))
    if (!n || !window.confirm('이 내부 드래프트를 삭제할까요?')) return
    await deleteCampaignDraft(CREATOR_ID, n).catch(() => {})
    if (selectedId === id) setSelectedId(null)
    await refreshDrafts()
  }

  // 기간 필터 (Calendar는 자체 월 뷰라 미적용) — 드래프트 포함
  const { filtered, tag: periodTag } = deriveCampaignPeriod(allCampaigns, period)
  const viewBoard: CampaignBoard = { ...board, campaigns: filtered }
  const showPeriod = !selected && view !== 'calendar'

  const TABS: { key: View; label: string }[] = [
    { key: 'board', label: 'Board' },
    { key: 'calendar', label: 'Calendar' },
    { key: 'history', label: 'History' },
  ]

  return (
    <div className="space-y-6">
      {/* 서브탭 바 (Board · Calendar · History) — 셸 상단바 하단 슬롯에 portal (타이틀 아래 합류) */}
      {!selected && headerSlot && createPortal(
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setView(t.key)}
                className={'-mb-px border-b-2 px-1 pb-3 text-sm font-semibold transition ' + (view === t.key ? 'border-brand-blue text-fg-1' : 'border-transparent text-fg-3 hover:text-fg-1')}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 pb-2.5">
            {showPeriod && (
              <div className="flex items-center gap-2">
                {periodTag && <span className="text-xs font-display tabular-nums text-fg-3">{periodTag}</span>}
                <div className="flex items-center border border-line-hairline bg-surface-base">
                  {PERIODS.map((p) => (
                    <button
                      key={p.key}
                      onClick={() => setPeriod(p.key)}
                      className={'px-2.5 py-1 text-xs font-medium ' + (period === p.key ? 'bg-ink-800 text-fg-inverse' : 'text-fg-3 hover:text-fg-1')}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button onClick={() => setNewOpen(true)} className="inline-flex items-center gap-1 bg-brand-blue px-3 py-1.5 text-xs font-semibold text-fg-inverse">
              <Plus className="h-3.5 w-3.5" aria-hidden /> New Campaign
            </button>
          </div>
        </div>,
        headerSlot,
      )}

      {/* 본문 */}
      {selected ? (
        <CampaignDetail c={selected} onBack={() => setSelectedId(null)} onChanged={refreshBoard} />
      ) : view === 'board' ? (
        <BoardView board={viewBoard} period={period} onOpen={open} onDeleteDraft={handleDeleteDraft} />
      ) : view === 'calendar' ? (
        <CalendarView events={board.schedule ?? []} />
      ) : (
        <HistoryView board={viewBoard} onOpen={open} />
      )}

      {/* 신규 등록 모달 (Products 와 동일 UX 패턴) */}
      {newOpen && <NewCampaignModal onCancel={() => setNewOpen(false)} onCreate={handleCreateDraft} />}
    </div>
  )
}
